
import { ApolloQueryResult } from 'apollo-client'
import { defineComponent } from 'vue'
import { OwnerPullRequestsQuery, Pr, RunStatus } from '~/types/types'
import UIInlineSwitcher from '~/components/Common/UI2/InlineSwitcher/UIInlineSwitcher.vue'
import PRListItem from './PRListItem.vue'
import SectionHeader from './SectionHeader.vue'
import OwnerPullRequestsGQL from '~/apollo/queries/owner/pullRequests.gql'

type GradeType = 'A' | 'B' | 'C' | 'D' | null

interface TransformedPR {
  id: string
  title: string
  status: RunStatus
  prNumber: string
  repoName: string
  isPrivate: boolean
  provider: string
  timestamp: string
  grade: GradeType
  issuesFixed?: number
  issuesIntroduced?: number
  issuesSuppressed?: number
  runId?: string
}

export default defineComponent({
  name: 'RecentPullRequestList',
  components: {
    UIInlineSwitcher,
    PRListItem,
    SectionHeader
  },
  data() {
    return {
      loading: true,
      filterOptions: ['You', 'Your team'],
      selectedFilter: 'Your team',
      pullRequests: [] as Pr[],
      fetchRequestId: 0
    }
  },
  async fetch(): Promise<void> {
    await this.fetchPullRequests()
  },
  computed: {
    isAuthorViewer(): boolean {
      return this.selectedFilter === 'You'
    },
    transformedPRs(): TransformedPR[] {
      return this.pullRequests.map((pr) => this.transformPR(pr))
    },
    emptyStateSubtitle(): string {
      return this.selectedFilter === 'You'
        ? 'Open a pull request on an activated repository to see it here.'
        : 'Pull requests from your team on activated repositories will appear here.'
    }
  },
  methods: {
    async fetchPullRequests(): Promise<void> {
      const requestId = ++this.fetchRequestId
      this.loading = true

      const { provider, owner } = this.$route.params

      try {
        const providerMeta = this.$providerMetaMap[provider]
        if (!providerMeta) return

        const response: ApolloQueryResult<OwnerPullRequestsQuery> = await this.$fetchGraphqlData(
          OwnerPullRequestsGQL,
          {
            login: owner,
            provider: providerMeta.value,
            isAuthorViewer: this.isAuthorViewer,
            first: 8
          }
        )

        if (requestId !== this.fetchRequestId) return

        const edges = response.data?.owner?.pullRequests?.edges || []
        this.pullRequests = edges
          .map((edge) => edge?.node)
          .filter((node): node is Pr => node !== null && node !== undefined)
      } catch (error) {
        if (requestId !== this.fetchRequestId) return
        this.$logErrorAndToast(error as Error, 'Failed to fetch pull requests.')
        this.pullRequests = []
      } finally {
        if (requestId === this.fetchRequestId) {
          this.loading = false
        }
      }
    },
    async onFilterChange(filter: string): Promise<void> {
      this.selectedFilter = filter
      await this.fetchPullRequests()
    },
    transformPR(pr: Pr): TransformedPR {
      const run = pr.latestAnalysisRun
      const repo = pr.repository

      return {
        id: pr.id,
        title: pr.title || '',
        status: (run?.status as RunStatus) || RunStatus.Pend,
        prNumber: `#${pr.number ?? ''}`,
        repoName: repo?.name || '',
        isPrivate: repo?.isPrivate || false,
        provider:
          repo?.vcsProvider && this.$providerMetaMap[repo.vcsProvider]
            ? this.$providerMetaMap[repo.vcsProvider].shortcode
            : '',
        timestamp: pr.modifiedAt || '',
        grade: this.mapGrade(run?.reportCard?.aggregate?.grade),
        issuesFixed: pr.resolvedCount || 0,
        issuesIntroduced: pr.raisedCount || 0,
        issuesSuppressed: pr.suppressedCount || 0,
        runId: run?.runId || undefined
      }
    },
    mapGrade(grade?: string | null): GradeType {
      if (grade === null || grade === undefined) return null
      const validGrades = ['A', 'B', 'C', 'D']
      return validGrades.includes(grade) ? (grade as GradeType) : null
    }
  }
})
